import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

export function ProductFilters({ 
  filters, 
  onFiltersChange, 
  showActive = true,
  showClear = true,
  className = "" 
}) {
  const [temporadas, setTemporadas] = useState([]);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Cargar temporadas únicas de la tabla products
  const fetchTemporadas = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('temporada')
        .not('temporada', 'is', null)
        .not('temporada', 'eq', '');

      if (error) {
        console.error('Error fetching temporadas:', error);
        return;
      }

      // Obtener valores únicos
      const uniqueTemporadas = [...new Set(data.map(item => item.temporada))].sort();
      setTemporadas(uniqueTemporadas);
    } catch (error) {
      console.error('Error fetching temporadas:', error);
    }
  };

  // Cargar todas las etiquetas
  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching tags:', error);
        return;
      }

      setTags(data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchTemporadas(), fetchTags()])
      .finally(() => setIsLoading(false));
  }, []);

  const handleTemporadaChange = (value) => {
    onFiltersChange({
      ...filters,
      temporada: value === "all" ? null : value
    });
  };

  const handleActiveChange = (value) => {
    onFiltersChange({
      ...filters,
      active: value === "all" ? null : (value === "true")
    });
  };

  const handleTagToggle = (tagId) => {
    const currentTagIds = filters.tagIds || [];
    const newTagIds = currentTagIds.includes(tagId)
      ? currentTagIds.filter(id => id !== tagId)
      : [...currentTagIds, tagId];

    onFiltersChange({
      ...filters,
      tagIds: newTagIds.length > 0 ? newTagIds : null
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      temporada: null,
      tagIds: null,
      active: null
    });
  };

  const hasActiveFilters = filters.temporada || filters.tagIds?.length > 0 || filters.active !== null;

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="text-sm text-muted-foreground">Cargando filtros...</div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-lg p-4 shadow-sm space-y-4 ${className}`}>
      {/* Header con título e indicador */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
          <h3 className="text-sm font-semibold text-slate-700">Filtros de Búsqueda</h3>
        </div>
        {hasActiveFilters && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
            {(filters.temporada ? 1 : 0) + (filters.tagIds?.length || 0) + (filters.active !== null ? 1 : 0)} activos
          </span>
        )}
      </div>

      {/* Filtros principales */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Filtro de Temporada */}
        <div className="flex items-center gap-2 bg-white rounded-md px-3 py-2 border border-slate-200 shadow-sm">
          <span className="text-sm font-medium text-slate-600">📅</span>
          <span className="text-sm font-medium text-slate-700">Temporada:</span>
          <Select value={filters.temporada || "all"} onValueChange={handleTemporadaChange}>
            <SelectTrigger className="w-[160px] border-0 shadow-none focus:ring-1 focus:ring-blue-500">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">
                  <span className="text-slate-500">•</span>
                  Todas las temporadas
                </span>
              </SelectItem>
              {temporadas.map((temporada) => (
                <SelectItem key={temporada} value={temporada}>
                  <span className="flex items-center gap-2">
                    <span className="text-blue-500">•</span>
                    {temporada}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Filtro de Estado Activo */}
        {showActive && (
          <div className="flex items-center gap-2 bg-white rounded-md px-3 py-2 border border-slate-200 shadow-sm">
            <span className="text-sm font-medium text-slate-600">⚡</span>
            <span className="text-sm font-medium text-slate-700">Estado:</span>
            <Select 
              value={filters.active === null ? "all" : filters.active.toString()} 
              onValueChange={handleActiveChange}
            >
              <SelectTrigger className="w-[120px] border-0 shadow-none focus:ring-1 focus:ring-blue-500">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-2">
                    <span className="text-slate-500">•</span>
                    Todos
                  </span>
                </SelectItem>
                <SelectItem value="true">
                  <span className="flex items-center gap-2">
                    <span className="text-green-500">•</span>
                    Activos
                  </span>
                </SelectItem>
                <SelectItem value="false">
                  <span className="flex items-center gap-2">
                    <span className="text-red-500">•</span>
                    Inactivos
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Botón Limpiar */}
        {showClear && hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="flex items-center gap-2 bg-white hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-all duration-200 shadow-sm"
          >
            <X className="h-4 w-4" />
            <span className="hidden sm:inline">Limpiar</span>
          </Button>
        )}
      </div>

      {/* Filtro de Etiquetas */}
      {tags.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">🏷️</span>
            <span className="text-sm font-medium text-slate-700">Etiquetas:</span>
            {filters.tagIds?.length > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                {filters.tagIds.length} seleccionadas
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto bg-white rounded-md p-3 border border-slate-200">
            {tags.map((tag) => {
              const isSelected = filters.tagIds?.includes(tag.id);
              return (
                <Badge
                  key={tag.id}
                  variant={isSelected ? "default" : "outline"}
                  className={`cursor-pointer transition-all duration-200 hover:scale-105 ${
                    isSelected 
                      ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm' 
                      : 'hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700'
                  }`}
                  onClick={() => handleTagToggle(tag.id)}
                >
                  {isSelected && <span className="mr-1">✓</span>}
                  {tag.name}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Resumen de filtros activos mejorado */}
      {hasActiveFilters && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <span className="text-blue-600 text-sm">ℹ️</span>
            <div className="space-y-1">
              <div className="text-sm font-medium text-blue-800">Filtros aplicados:</div>
              <div className="flex flex-wrap gap-2 text-xs">
                {filters.temporada && (
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    📅 {filters.temporada}
                  </span>
                )}
                {filters.tagIds?.length > 0 && (
                  <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                    🏷️ {filters.tagIds.length} etiqueta{filters.tagIds.length !== 1 ? 's' : ''}
                  </span>
                )}
                {filters.active !== null && (
                  <span className={`px-2 py-1 rounded-full ${
                    filters.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    ⚡ {filters.active ? 'Activos' : 'Inactivos'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

